import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { IFilterOption } from '../../interface/transaction';
import { getNoteFilterByEntity, NotesEntity, SignificanceTypeValue } from '../../constants/notes';
import { Spin } from '../Ant';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { INote } from '../../interface/notes';
import NotesCard from '../dashboard/transactionView/notesCard';
import { StickyNoteEmpty } from '../icons';
import NoData from '../dashboard/noData';
import { useFetchNotes } from '../../hooks/useFetchNotes';
import { ROUTES } from '../../constants/constants';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { sortBy } from 'lodash';
import { IActiveSortState, ISortOption } from '../../interface/common';
import SortSelector from '../sortPopover/sortSelector';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const Notes = ({ entity, entitySubject }: { entity: NotesEntity; entitySubject: string }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { push } = useNavigationStack();
  const queryClient = useQueryClient();
  const notesFilterOptions: IFilterOption[] = [
    { label: t('note:significance.normal'), value: SignificanceTypeValue.normal },
    { label: t('note:significance.important'), value: SignificanceTypeValue.important },
    { label: t('note:significance.archived'), value: SignificanceTypeValue.archived },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('notesSearch') || '';
  const initialFilterValue = searchParams.get('notesFilter') || '';
  const initialFilterValues = initialFilterValue ? initialFilterValue.split(',') : [];
  const initialSelectedFilter = notesFilterOptions.filter((opt) =>
    initialFilterValues.includes(opt.value),
  );
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [selectedFilter, setSelectedFilter] = useState<IFilterOption[]>(initialSelectedFilter);
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'modifiedOn',
    direction: 'DESC',
  });
  const noteSortOptions: ISortOption[] = useMemo(
    () => [
      { field: 'modifiedOn', label: t('note:modifiedOn'), type: 'date', defaultDirection: 'DESC' },
    ],
    [t],
  );
  const {
    data: notesList,
    isLoading: isNotesLoading,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchNotes(
    {
      extraFilters: {
        ...(selectedFilter?.length
          ? { significance: `$in:${selectedFilter.map((x) => x.value).join(',')}` }
          : {}),
        ...getNoteFilterByEntity(entity, id ?? ''),
      },
      sortBy: [`${activeSort.field}:${activeSort.direction}`],
      search: searchValue,
      relations: ['createdBy:fullName'],
      pageSize: 10,
    },
    {
      enabled: !!id,
    },
  );
  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && notesList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const handleAddNote = () => {
    push(ROUTES.ADD_EDIT_NOTES, {
      entity,
      entityId: id,
    });
  };
  const sortingOrder = [
    SignificanceTypeValue.important,
    SignificanceTypeValue.normal,
    SignificanceTypeValue.archived,
  ];
  const paginatedNotesSortedBySignificance = sortBy(notesList, (obj) => {
    return sortingOrder.indexOf(obj?.significance);
  });
  useEffect(() => {
    setSearchParams({
      notesSearch: searchValue,
      notesFilter: selectedFilter.map((f) => f.value).join(','),
    });
  }, [searchValue, selectedFilter]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" data-testid="small-indicator" />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="notes-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('note:search')}
          filterTitle={t('note:note')}
          searchValue={searchValue}
          checkboxOptions={notesFilterOptions}
          handleSearch={(val: string) => {
            setSearchValue(val);
          }}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['notes-list'] });
            return refetch();
          }}
          clearFilter={(val?: string) => {
            if (val) {
              setSelectedFilter((prev) => prev.filter((item) => item.value !== val));
            } else {
              setSelectedFilter([]);
            }
          }}
          selectedFilter={selectedFilter}
          setSelectedCheckboxFilter={(val: IFilterOption[]) => {
            setSelectedFilter(val);
          }}
          handleAdd={handleAddNote}
          tooltipTextForAdd={t('note:addNote')}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={noteSortOptions}
            onApply={(sort) => setActiveSort(sort)}
            activeSort={activeSort}
          />
        </SearchInputWithFilters>
      }
    >
      {paginatedNotesSortedBySignificance &&
        paginatedNotesSortedBySignificance.length > 0 &&
        paginatedNotesSortedBySignificance.map((note: INote) => (
          <NotesCard key={note.id} detailedView={false} note={note} entitySubject={entitySubject} />
        ))}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!notesList || (Object.entries(notesList).length === 0 && !isNotesLoading)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData header={t('note:noNotes')} subHeader="" icon={<StickyNoteEmpty />} />
        </div>
      )}
      {isNotesLoading ? <SkeletonCardList rows={[1, 2]} /> : renderLoader()}
    </CollapsibleListLayout>
  );
};

export default Notes;
